import { useState, useEffect, useRef } from 'react';

const useQuery = <T,>(queryFn: () => Promise<T>) => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isPending, setIsPending] = useState<boolean>(true);
  const [isRefetching, setIsRefetching] = useState<boolean>(false);
  const [complete, setComplete] = useState<boolean>(false);
  const [refetchIndex, setRefetchIndex] = useState<number>(0);

  const previousRefetchIndex = useRef(refetchIndex);

  useEffect(() => {
    let isMounted = true; // Prevent state updates on unmounted components

    // A manual refetch keeps the existing data mounted; only an initial/new query shows the full loader
    const isRefetch = previousRefetchIndex.current !== refetchIndex;
    previousRefetchIndex.current = refetchIndex;

    const fetchData = async () => {
      if (isRefetch) {
        setIsRefetching(true);
      } else {
        setIsPending(true);
      }
      setError(null);
      setComplete(false);

      try {
        const result = await queryFn();
        if (isMounted) {
          setData(result);
          setComplete(true);
        }
      } catch (err) {
        if (isMounted) setError(err as Error);
      } finally {
        if (isMounted) {
          setIsPending(false);
          setIsRefetching(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [queryFn, refetchIndex]);

  const refetch = () => setRefetchIndex(prev => prev + 1);

  return { data, error, isPending, isRefetching, complete, refetch };
};

export default useQuery;
